import { useEffect, useState } from "react";
import { C, roomTypes, furniture as furnitureKinds, annexes } from "@bd/content";
import { boxArt, roomThumb, furnitureSprite } from "@bd/art";
import { dateOf, money, levelCap, yearOf, type Company, type Project, eraFor, priceIndex, leagueRank, goalProgress, shelfSpace, fanMarket } from "@bd/sim";
import { CountUp, Icon, Logo, Meter, Why } from "@bd/ui-kit";
import { me, useGame, type Tool } from "../store";
import { dataUrl } from "../world/textures";
import { fmtMoney, k, researchLines, roomStats, teamMorale, weeklyCashLines } from "../lib/why";
import { SPEED_LABELS } from "../sim/protocol";
import { studio } from "../world/World";
import { sfx } from "../lib/sound";
import { t, tp, tdate } from "../i18n";

export function phaseLabel(p: Project): { text: string; prog: number } {
  switch (p.phase) {
    case "prototype":
      return { text: t("Prototype {pct}%", { pct: Math.round((p.work.prototype.done / p.work.prototype.need) * 100) }), prog: p.work.prototype.done / p.work.prototype.need };
    case "playtest":
      return { text: p.playtests.length ? t("Playtest {n}", { n: p.playtests.length }) : t("Ready to playtest"), prog: 0.4 };
    case "art":
    case "rulebook": {
      const done = p.work.art.done + p.work.rulebook.done;
      const need = p.work.art.need + p.work.rulebook.need;
      return { text: p.fixing.length ? t("Fixing") : t("Art & rules"), prog: done / need };
    }
    default:
      return { text: t("Ready to print"), prog: 1 };
  }
}

/** A game's box (96 × 72 pixels, then scaled by whole steps so the pixels stay crisp). The audience picks the frame. */
export function cover(title: string, theme: string, mechanic: string, color?: string, quality?: number, scale = 1, award = false, audience?: string) {
  return dataUrl(boxArt({ title, theme, mechanic, publisherColor: color, quality, award, audience }), scale);
}

/** Did this game win at a Dicefall con? (its box gets the gold seal) */
export function wonAward(game: { market: { awards?: { game: string }[] } }, name: string): boolean {
  return (game.market.awards ?? []).some((a) => a.game === name);
}

// ------------------------------------------------------------------ top left: brand and projects

export function BrandAndProjects() {
  // on phones the list folds into a chip so the studio stays in view
  const [folded, setFolded] = useState(() => window.innerWidth < 760);
  const game = useGame((s) => s.game);
  // bars glide across the week instead of jumping once a week
  const glide = useGame((s) => (s.speed > 0 ? s.msPerWeek : 0));
  const set = useGame((s) => s.set);
  const co = me(game);
  if (!co) return null;
  const printing = co.products.filter((p) => !p.launched && p.orders.length);
  const rows = [
    ...co.projects.map((p) => ({ id: p.id, name: p.design.name, theme: p.design.theme, mech: p.design.mechanic, aud: p.design.audience, ...phaseLabel(p), ...((p.phase === "prototype" || p.phase === "art" || p.phase === "rulebook") && !co.staff.some((s) => s.projectId === p.id) ? { text: t("Nobody on it"), empty: true } : {}), open: () => set({ screen: p.phase === "ready" ? { kind: "print", projectId: p.id } : p.phase === "playtest" ? { kind: "playtest", projectId: p.id } : { kind: "design", projectId: p.id } }), alert: p.phase === "playtest" || p.phase === "ready", team: teamLine(co.staff.filter((s) => s.projectId === p.id)) })),
    ...printing.map((p) => ({ id: p.id, team: undefined as ReturnType<typeof teamLine>, name: p.design.name, theme: p.design.theme, mech: p.design.mechanic, aud: p.design.audience, text: t("Printing · {n} wk", { n: Math.max(0, p.orders[0].arriveWeek - game!.week) }), prog: 0.9, open: () => set({ screen: { kind: "products" } }), alert: false })),
  ];
  const idle = co.staff.filter((s) => !s.projectId && !s.training && (s.awayUntil ?? 0) <= game!.week).length;
  return (
    <div className="hud-tl">
      <div className="panel pill brand" data-testid="brand">
        <Logo size={36} />
        <span className="display" style={{ fontSize: "1.55rem" }}>Box &amp; Dice</span>
      </div>
      <div className={`panel projects ${folded ? "folded" : ""}`} data-testid="projects">
        <div className="spread">
          <button className="res-btn projects-toggle" onClick={() => setFolded((f) => !f)} aria-expanded={!folded} aria-label={t("Projects")} title={t("Projects")} data-testid="projects-toggle">
            <Icon name="projects" size={18} />
            {folded ? <span className="num">{rows.length}</span> : <span>{t("Projects")}</span>}
            {folded && rows.some((r) => r.alert) && <span className="dot-alert" aria-label={t("A project needs you")} />}
          </button>
          {!folded && co.stats.released > 0 && (() => {
            const sh = shelfSpace(game!, co);
            const full = sh.recent >= sh.slots;
            return <span className={`ichip plain ${full ? "warnc" : "muted"}`} title={`${t("Shop slots {used}/{slots}", { used: Math.min(sh.recent, sh.slots), slots: sh.slots })}${full ? t(" · full") : ""}. ${t("Shops take only a few new games from you a year. Past that, each gets less shelf space; from an office on, they charge to list it.")}`} data-testid="shelf-slots"><Icon name="box" size={14} />{Math.min(sh.recent, sh.slots)}/{sh.slots}</span>;
          })()}
          <button className="btn small ghost" onClick={() => set({ screen: { kind: "design" } })} title={t("New game")} data-testid="new-game-btn">
            <Icon name="plus" size={16} /> <span className="new-game-word">{t("New game")}</span>
          </button>
        </div>
        {rows.map((r) => (
          <button key={r.id} className="project-row" onClick={r.open} data-testid="project-row">
            <img src={cover(r.name, r.theme, r.mech, co.color, 0.6, 1, false, r.aud)} alt="" width={40} height={30} style={{ imageRendering: "auto", borderRadius: 4 }} />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span title={r.name} style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", fontWeight: 600, lineHeight: 1.2 }}>{r.name}</span>
              <span className="spread" style={{ gap: 6, marginTop: 2 }}>
                {r.team ? <span className="muted ichip plain" style={{ fontSize: "0.75rem" }} title={r.team.title} data-testid="project-team"><Icon name="staff" size={12} />{r.team.n}</span> : <span />}
                <span className={"empty" in r && r.empty ? "bad" : "muted"} style={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                  {r.alert && <span className="dot-alert" aria-label={t("Needs you")} />} {r.text}
                </span>
              </span>
              <Meter value={r.prog} color={r.alert ? "var(--coral)" : "var(--ink)"} style={{ height: 3, marginTop: 4 }} glideMs={glide} />
            </span>
          </button>
        ))}
        {idle > 0 && !folded && co.projects.length > 0 && (
          <button className="project-row bad" style={{ fontSize: "0.8rem" }} onClick={() => set({ screen: { kind: "staff" } })} data-testid="idle-staff">
            <span className="ichip plain bad" title={tp(idle, "{n} person idle: start a game", "{n} people idle: start a game")}><Icon name="staff" size={14} />{idle} <span>{t("idle")}</span></span>
          </button>
        )}
        {(co.goals ?? []).length > 0 && (
          <div className="stack" style={{ gap: 4, marginTop: 10, paddingTop: 8, borderTop: "1px solid var(--line)" }} data-testid="goals">
            <span className="spread muted" title={`${t("Goals for {year}", { year: Math.floor(game!.week / 52) + 1965 })}. ${t("Meet them all: a clean sweep wins a trophy")}`}>
              <span className="ichip plain muted"><Icon name="star" size={14} />{Math.floor(game!.week / 52) + 1965}</span>
              {(co.trophies ?? []).length > 0 && <span className="ichip plain muted" title={t("Clean sweeps: years you met every goal")} data-testid="trophies"><Icon name="trophy" size={13} color="var(--gold)" />{co.trophies!.length}</span>}
            </span>
            {co.goals!.filter((g) => !g.done).map((g) => {
              const p = goalProgress(game!, co, g);
              const prog = g.done ? 1 : g.id === "copies" || g.id === "research" || g.id === "grow" ? Math.min(1, p.value / g.target) : g.id === "score" ? Math.min(1, p.value / g.target) : 0;
              return (
                <span key={g.id} className="row" style={{ gap: 6, fontSize: "0.8rem", alignItems: "flex-start" }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    {goalText(g)}
                    {prog > 0 && <Meter value={prog} color="var(--teal)" style={{ height: 2, marginTop: 3 }} />}
                  </span>
                </span>
              );
            })}
            {co.goals!.some((g) => g.done) && (
              <span className="row muted" style={{ gap: 6, fontSize: "0.75rem" }} title={co.goals!.filter((g) => g.done).map((g) => goalText(g)).join("\n")} data-testid="goals-done">
                <Icon name="check" size={14} color="var(--green)" /> {co.goals!.filter((g) => g.done).length}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ top centre: resources

export function Resources() {
  const game = useGame((s) => s.game);
  const set = useGame((s) => s.set);
  const co = me(game);
  if (!co || !game) return null;
  const cash = weeklyCashLines(co);
  const net = cash.total; // the same number the breakdown adds up to
  const morale = teamMorale(co);
  const you = co.staff[0];
  return (
    <div className="panel pill hud-res" data-testid="resources">
      <span className="res" title={t("Cash")}>
        <Icon name="coin" color="var(--gold)" />
        <button className="num big res-btn" onClick={() => set({ screen: { kind: "finances" } })}><CountUp value={co.cash} fmt={fmtMoney} /></button>
        <span className={`num small ${net >= 0 ? "good" : "bad"}`}>{net >= 0 ? "+" : "−"}{k(Math.abs(net))}</span>
        <Why title={t("Last week")} lines={cash.lines} total={cash.total} fmt={fmtMoney} note={t("Printing, hiring and building are paid up front, so they aren't here.")} />
      </span>
      <span className="sep" />
      <span className="res" title={t("Fans")}>
        <Icon name="fans" color="var(--coral)" />
        <button className="num big res-btn" onClick={() => set({ screen: { kind: "market" } })}>{k(co.fans)}</button>
        <Why title={t("Fans")} lines={[{ label: t("Fans"), value: k(co.fans) }, { label: t("Extra sales from fans"), value: `+${Math.round(((co.fans / (co.fans + fanMarket(game!.week))) * 0.5 + 0.08 * Math.log10(1 + co.fans / (fanMarket(game!.week) * 15))) * 100)}%` }, { label: t("Reputation"), value: `${Math.round(co.reputation)} / 100` }]} note={t("Good games win fans. Bad ones lose them.")} />
      </span>
      <span className="sep" />
      <span className="res" title={t("Research points")}>
        <Icon name="research" color="#5a8cdc" />
        <button className="num big res-btn" onClick={() => set({ screen: { kind: "research" } })}>{Math.floor(co.research)}</button>
        <Why title={t("Research points")} lines={researchLines(co)} />
      </span>
      <span className="sep" />
      <span className="res" title={t("Team morale")}>
        <Icon name="heart" color="var(--coral)" />
        <button className="num big res-btn" onClick={() => set({ screen: { kind: "staff" } })}>{Math.round(morale)}%</button>
        {you && <Why title={t("Team morale")} lines={[{ label: t("Team average"), value: `${Math.round(morale)}%` }, ...[...co.staff].sort((a, b) => a.morale - b.morale).slice(0, co.staff.length > 3 ? 3 : co.staff.length).map((s) => ({ label: co.staff.length > 3 ? t("Lowest: {name}", { name: s.founder ? t("you") : s.name }) : s.founder ? t("you") : s.name, value: `${Math.round(s.morale)}%` }))]} note={t("Pay, rooms, decor, pets and hits raise it. Staff shows each person's reasons.")} />}
      </span>
    </div>
  );
}

// ------------------------------------------------------------------ top right: date and speed

export function Clock() {
  const game = useGame((s) => s.game);
  const speed = useGame((s) => s.speed);
  const setSpeed = useGame((s) => s.setSpeed);
  const pausedFor = useGame((s) => s.pausedFor);
  const set = useGame((s) => s.set);
  if (!game) return null;
  const d = dateOf(game.week);
  const co = me(game);
  const rank = co && co.stats.released > 0 ? leagueRank(game, co) : null;
  return (
    <div className="hud-tr">
      <div className="panel pill clock" data-testid="clock">
        {/* the date; the week and the era are on hover, the league is a trophy chip */}
        <div style={{ minWidth: 0 }} title={`${t("Week {n}", { n: d.weekOfYear })} · ${t(eraFor(game, game.week).name)}${t(" era")}`} data-testid="clock-date">
          <div style={{ fontWeight: 600 }}>{tdate(d.label)}{game.settings.newGamePlus ? " +" : ""}</div>
          {pausedFor && speed === 0 && (
            <div style={{ fontSize: "0.78rem", color: "var(--coral)", fontWeight: 600 }} role="status" title={t("Paused · {reason}", { reason: t(pausedFor) })} data-testid="paused-note">{t(pausedFor)}</div>
          )}
        </div>
        {rank && game.week >= 52 && (
          <button className="ichip clock-league" onClick={() => set({ screen: { kind: "market", tab: "league" } })} title={t("Publisher league: #{rank} of {of}, by a year of sales", { rank: rank.rank, of: rank.of })} aria-label={t("League #{rank}", { rank: rank.rank })} data-testid="league-rank">
            <Icon name="trophy" size={14} color="var(--gold)" />#{rank.rank}
          </button>
        )}
        <div className="speed inset" role="group" aria-label={t("Game speed")}>
          {(["pause", "play", "ff", "fff"] as const).map((ic, i) => (
            <button key={ic} className={`speed-btn ${speed === i ? "on" : ""}`} onClick={() => setSpeed(i)} aria-label={t(SPEED_LABELS[i])} aria-pressed={speed === i} data-testid={`speed-${i}`}>
              <Icon name={ic} size={18} color={speed === i ? "#fff" : "var(--ink)"} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ toasts

export function Toasts() {
  const toasts = useGame((s) => s.toasts);
  const dismiss = useGame((s) => s.dismiss);
  const set = useGame((s) => s.set);
  const full = useGame((s) => !!s.closeup || s.screen?.kind === "playtest" || s.screen?.kind === "launch" || s.screen?.kind === "finale");
  const color = { good: "var(--green)", bad: "var(--bad)", info: "var(--teal)", warn: "var(--warn)" };
  return (
    <div className={`toast-stack ${full ? "centre" : ""}`} aria-live="polite">
      {toasts.map((x) => (
        <div key={x.id} className="panel toast" data-testid="toast">
          <span className="dot" style={{ background: color[x.tone] }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{x.title}</div>
            {x.text && <div className="muted" style={{ fontSize: "0.85rem" }}>{x.text}</div>}
            {x.action && (
              <button className="btn small" style={{ marginTop: 6 }} onClick={() => { set({ screen: x.action!.screen }); dismiss(x.id); }}>
                {x.action.label}
              </button>
            )}
          </div>
          <button className="icon-btn" style={{ width: 28, height: 28 }} onClick={() => dismiss(x.id)} aria-label={t("Dismiss")}>
            <Icon name="close" size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ bottom: dock and tray

const DOCK: { tool: Tool; icon: string; label: string }[] = [
  { tool: "build", icon: "build", label: "Build" },
  { tool: "furnish", icon: "furnish", label: "Furnish" },
  { tool: "staff", icon: "staff", label: "Staff" },
  { tool: "projects", icon: "projects", label: "Games" },
  { tool: "research", icon: "research", label: "Research" },
  { tool: "market", icon: "market", label: "Market" },
  { tool: "overlays", icon: "money", label: "Money" },
];

/** Phones: one thumb-sized speed button above the dock (pause → normal → fast → faster). */
export function SpeedFab() {
  const speed = useGame((s) => s.speed);
  const setSpeed = useGame((s) => s.setSpeed);
  const busy = useGame((s) => !!s.tool || !!s.selectedRoom || !!s.screen);
  if (busy) return null;
  const next = speed >= 3 ? 0 : speed + 1;
  const icon = (["pause", "play", "ff", "fff"] as const)[Math.min(3, speed)];
  return (
    <button className="speed-fab" onClick={() => setSpeed(next)} aria-label={t("Game speed: {now}. Tap for {next}", { now: t(SPEED_LABELS[speed]), next: t(SPEED_LABELS[next]) })} data-testid="speed-fab">
      <Icon name={icon} size={22} color="#fff" />
    </button>
  );
}

export function Dock() {
  const tool = useGame((s) => s.tool);
  const set = useGame((s) => s.set);
  const online = useGame((s) => !!s.online);
  const choose = (to: Tool) => {
    if (to === "staff") return set({ screen: { kind: "staff" }, tool: null, placing: null });
    if (to === "projects") return set({ screen: { kind: "products" }, tool: null, placing: null });
    if (to === "market") return set({ screen: { kind: "market" }, tool: null, placing: null });
    if (to === "research") return set({ screen: { kind: "research" }, tool: null, placing: null });
    if (to === "overlays") return set({ screen: { kind: "finances" }, tool: null, placing: null });
    set({ tool: tool === to ? null : to, placing: null, buildGhost: null, trayTab: to === "furnish" ? "furniture" : "rooms" });
  };
  return (
    <nav className="panel pill dock" aria-label={t("Tools")} data-testid="dock">
      {DOCK.map((d) => (
        <button key={d.label} className={`dock-btn ${tool === d.tool ? "on" : ""}`} onClick={() => choose(d.tool)} aria-pressed={tool === d.tool} data-testid={`dock-${d.label.toLowerCase()}`}>
          <Icon name={d.icon} color={tool === d.tool ? "#fff" : "var(--ink)"} />
          <span className="dock-label">{t(d.label)}</span>
        </button>
      ))}
      {online && (
        <button className="dock-btn" onClick={() => set({ screen: { kind: "cousins" }, tool: null, placing: null })} data-testid="dock-cousins">
          <Icon name="fans" />
          <span className="dock-label">{t("Cousins")}</span>
        </button>
      )}
      <button className="dock-btn" onClick={() => set({ screen: { kind: "menu" } })} aria-label={t("Menu")} data-testid="dock-menu">
        <Icon name="menu" />
        <span className="dock-label">{t("Menu")}</span>
      </button>
    </nav>
  );
}

const DECOR_KINDS = new Set(["rug", "rug_round", "poster", "corkboard", "whiteboard", "lamp_desk", "lamp_floor", "lamp_hanging", "lamp_cage", "lamp_string", "sign", "trophy_shelf", "display_case", "golden_meeple", "hall_of_hits", "founders_portrait"]);
const PLANT_KINDS = new Set(["plant_pot", "plant_big", "planter", "tree", "fountain", "bird_bath", "pergola", "pet_bed", "cat_tree"]);

export function Tray() {
  const tool = useGame((s) => s.tool);
  const tab = useGame((s) => s.trayTab);
  const placing = useGame((s) => s.placing);
  const game = useGame((s) => s.game);
  const set = useGame((s) => s.set);
  const co = me(game);
  if (!co || !game || (tool !== "build" && tool !== "furnish")) return null;
  const year = yearOf(game.week);
  const idx = Math.pow(1.028, game.week / 52);
  const tabs = [
    { id: "rooms", label: t("Rooms") },
    { id: "furniture", label: t("Furniture") },
    { id: "decor", label: t("Decor") },
    { id: "plants", label: t("Plants & pets") },
  ] as const;
  const lvl = C.level[co.level];
  let cards: { id: string; name: string; icon?: string; tip?: string; price: string; img: string; locked: boolean; lockWhy?: string; onPick: () => void; sel: boolean; isNew?: boolean; facts?: Fact[]; note?: string }[] = [];
  if (tab === "rooms") {
    const order = ["garage", "office", "studio", "hq"];
    const next = order[order.indexOf(co.level) + 1];
    if (next) {
      const nl = C.level[next];
      cards.push({
        id: `move-${next}`, name: t(nl.name), icon: "trend", tip: t("Move up: {level}", { level: t(nl.name) }), price: money(nl.moveCost * idx), img: dataUrl(roomThumb("break_room"), 2), locked: false,
        sel: false, isNew: co.staff.length >= levelCap(co), onPick: () => set({ screen: { kind: "finances" }, tool: null }),
      });
    }
    const order2 = ["garage", "office", "studio", "hq"];
    const nextAnnex = annexes.find((a) => !(co.annexes ?? []).some((x) => x.kind === a.id) && order2.indexOf(co.level) >= order2.indexOf(a.level) && (!a.year || yearOf(game.week) >= a.year));
    if (nextAnnex) cards.push({
      id: `annex-${nextAnnex.id}`, name: t(nextAnnex.name), icon: "plus", tip: t("Buy: {place}", { place: t(nextAnnex.name) }), price: money(nextAnnex.cost * idx), img: dataUrl(roomThumb(nextAnnex.rooms[0]), 2), locked: false,
      sel: false, isNew: co.staff.length >= levelCap(co), onPick: () => {
        // show the building itself, with its card
        studio?.focusLot(nextAnnex.id);
        set({ selectedLot: nextAnnex.id, selectedRoom: null, tool: null });
      },
    });
    cards.push(...roomTypes.map((r) => {
      const locked = !lvl.rooms.includes(r.id);
      return {
        id: r.id, name: t(r.name), price: locked ? t("{level} only", { level: t(C.level[r.level].name) }) : t("from {price}", { price: money(r.cost * idx) }), img: dataUrl(roomThumb(r.id, locked), 2), locked, lockWhy: t("Needs a {level}", { level: t(C.level[r.level].name).toLowerCase() }),
        sel: placing?.kind === "room" && placing.type === r.id, isNew: !locked && !co.rooms.some((x) => x.type === r.id) && r.level === co.level,
        onPick: () => set({ placing: { kind: "room", type: r.id }, selectedRoom: null }),
      };
    }));
  } else {
    const kinds = furnitureKinds.filter((f) => (tab === "decor" ? DECOR_KINDS.has(f.id) : tab === "plants" ? PLANT_KINDS.has(f.id) : !DECOR_KINDS.has(f.id) && !PLANT_KINDS.has(f.id)));
    // reward pieces show only in Decor, and only once you've placed none of them yet
    const placed = (id: string) => co.rooms.some((r) => r.furniture.some((x) => x.kind === id));
    cards = kinds.filter((f) => !f.sweeps || (tab === "decor" && !placed(f.id))).map((f) => {
      const sweeps = (co.trophies ?? []).length;
      const reward = !!f.sweeps;
      const locked = (!!f.year && year < f.year) || (reward && sweeps < f.sweeps!);
      const lockWhy = reward ? tp(f.sweeps!, "Reward for {n} clean sweep ({have} so far)", "Reward for {n} clean sweeps ({have} so far)", { have: sweeps }) : t("Arrives in {year}", { year: f.year! });
      return {
        id: f.id, name: t(f.name), price: reward ? (locked ? t("Reward") : t("Free")) : locked ? `${f.year}` : money(f.cost * idx), img: dataUrl(furnitureSprite(f.id, 0, 0, yearOf(game.week)).img, 2), locked, lockWhy, note: furnitureNote(f), facts: furnitureFacts(f),
        sel: placing?.kind === "furniture" && placing.type === f.id, onPick: () => set({ placing: { kind: "furniture", type: f.id } }),
      };
    });
  }
  return (
    <div className={`panel tray ${placing ? "placing" : ""}`} data-testid="tray">
      <div className="spread" style={{ marginBottom: 8 }}>
        <div className="tabs">
          {tabs.map((x) => (
            <button key={x.id} className={`tab ${tab === x.id ? "on" : ""}`} onClick={() => set({ trayTab: x.id, placing: null })}>{x.label}</button>
          ))}
        </div>
        {placing && (
          <span className="muted tray-hint ichip plain" style={{ fontSize: "0.85rem", fontFamily: "var(--f-ui)", fontWeight: 500 }}>
            <Icon name="info" size={14} />{tab === "rooms" ? t("Drag on empty floor") : t("Click inside a room · R rotates")}
          </span>
        )}
      </div>
      <div className="tray-cards">
        {cards.map((c) => (
          <button key={c.id} className={`card tray-card lift ${c.sel ? "sel" : ""}`} disabled={c.locked} title={c.locked ? c.lockWhy : c.note ? `${c.name}: ${c.note}` : c.tip ?? c.name} onClick={c.onPick} data-testid={`tray-${c.id}`}>
            {c.isNew && <span className="badge" style={{ position: "absolute", left: 8, top: 8 }}>{t("NEW")}</span>}
            {c.locked && <span style={{ position: "absolute", right: 8, top: 8 }}><Icon name="lock" size={14} color="var(--muted)" /></span>}
            <img src={c.img} alt="" style={{ imageRendering: "pixelated", height: 58, maxWidth: "100%", objectFit: "contain" }} />
            <span className="tray-name">{c.icon && <Icon name={c.icon} size={14} style={{ verticalAlign: -2, marginRight: 3 }} />}{c.name}</span>
            {c.facts && c.facts.length > 0 && (
              <span className="ichips tray-facts">
                {c.facts.map((x, i) => <span key={i} className="ichip plain muted" title={x.title}><Icon name={x.icon} size={12} />{x.v}</span>)}
              </span>
            )}
            <span className={`num ${c.sel ? "" : ""}`} style={{ color: c.sel ? "var(--coral)" : c.locked ? "var(--muted)" : "var(--ink)" }}>{c.price}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ the build price card

export function BuildCard() {
  const ghost = useGame((s) => s.buildGhost);
  const placing = useGame((s) => s.placing);
  const set = useGame((s) => s.set);
  const cmd = useGame((s) => s.cmd);
  const toast = useGame((s) => s.toast);
  if (!ghost || placing?.kind !== "room") return null;
  const rt = C.room[ghost.type];
  const left = Math.min(window.innerWidth - 300, Math.max(12, ghost.screen.x + 26));
  const top = Math.min(window.innerHeight - 220, Math.max(90, ghost.screen.y - 54));
  const build = async () => {
    const r = await cmd({ type: "buildRoom", room: ghost.type, x: ghost.x, y: ghost.y, w: ghost.w, h: ghost.h });
    if (!r.ok) toast({ title: t("Can't build that"), text: r.error, tone: "warn" });
    else {
      sfx("build");
      set({ placing: null, buildGhost: null, selectedRoom: r.id ?? null });
    }
  };
  return (
    <div className="panel build-card" style={{ left, top }} data-testid="build-card">
      <div className="spread">
        <strong style={{ fontSize: "1.1rem" }}>{t(rt.name)}</strong>
        <span className="num" style={{ fontSize: "1.15rem" }}>{money(ghost.price)}</span>
      </div>
      <div className="muted" style={{ fontSize: "0.88rem" }} title={t("{w} × {h} tiles · drag to redraw", { w: ghost.w, h: ghost.h })}>{t("{w} × {h} tiles", { w: ghost.w, h: ghost.h })}</div>
      <div className="effect-chip">{t(rt.effect)}</div>
      {!ghost.valid && <div className="bad" style={{ fontSize: "0.85rem", marginTop: 4 }}>{ghost.why}</div>}
      <div className="row" style={{ marginTop: 10 }}>
        <button className="btn small teal" disabled={!ghost.valid} onClick={build} data-testid="build-confirm"><Icon name="build" size={16} />{t("Build")}</button>
        <button className="icon-btn" style={{ width: 32, height: 32, marginLeft: "auto" }} onClick={() => set({ placing: null, buildGhost: null })} aria-label={t("Cancel")} title={t("Cancel")}><Icon name="close" size={16} /></button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ right: the selected room

export function RoomCard() {
  const roomId = useGame((s) => s.selectedRoom);
  const [confirm, setConfirm] = useState(false);
  useEffect(() => setConfirm(false), [roomId]);
  const game = useGame((s) => s.game);
  const set = useGame((s) => s.set);
  const cmd = useGame((s) => s.cmd);
  const co = me(game);
  const tool = useGame((s) => s.tool);
  if (!roomId || !co) return null;
  // on phones the build tray and the room card share the bottom of the screen: one at a time
  if ((tool === "build" || tool === "furnish") && window.innerWidth < 760) return null;
  const r = co.rooms.find((x) => x.id === roomId);
  if (!r) return null;
  const st = roomStats(co, roomId);
  // the project most of this room's people are working on
  const here = new Map<string, number>();
  for (const sid of studio?.staffInRoom(r.id) ?? []) {
    const pid = co.staff.find((x) => x.id === sid)?.projectId;
    if (pid) here.set(pid, (here.get(pid) ?? 0) + 1);
  }
  const topId = [...here.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
  const project = co.projects.find((p) => p.id === topId);
  const canZoom = r.type === "playtest_lab" || r.type === "design";
  const canDemolish = r.type !== "design" || co.rooms.filter((x) => x.type === "design").length > 1;
  return (
    <div className="panel room-card" data-testid="room-card">
      <div className="spread" style={{ alignItems: "flex-start" }}>
        <h3 className="display" style={{ margin: 0, fontSize: "1.5rem", minWidth: 0 }} title={`${t("{w} × {h} tiles", { w: r.w, h: r.h })} · ${t(st.type.effect)}`}>{t(st.type.name)}</h3>
        <button className="icon-btn" style={{ width: 30, height: 30, flex: "none" }} onClick={() => set({ selectedRoom: null })} aria-label={t("Close")} data-testid="room-close"><Icon name="close" size={14} /></button>
      </div>
      <div className="ichips" style={{ marginTop: 8 }}>
        <span className="ichip" title={r.type === "playtest_lab" ? t("Tester seats") : t("Desks")}><Icon name="staff" size={14} />{r.type === "playtest_lab" ? Math.min(6, st.seats * 4) : st.seats}</span>
        <span className="ichip good" title={t("Furnished")}><Icon name="furnish" size={14} />{Math.round((0.6 + 0.4 * Math.min(1, r.furniture.length / 6)) * 100)}%<Why title={t(st.type.name)} lines={[{ label: t(st.type.effect), value: "" }, { label: t("Furniture"), value: tp(r.furniture.length, "{n} piece", "{n} pieces") }]} note={t("A bare room works at 60%. Six pieces of furniture make it 100%.")} /></span>
        {project && <span className="ichip plain muted" style={{ maxWidth: "100%", minWidth: 0 }} title={project.design.name}><Icon name="projects" size={14} /><span className="ellipsis">{project.design.name}</span></span>}
      </div>
      <div className="row" style={{ marginTop: 12, flexWrap: "wrap", gap: 6 }}>
        {canZoom && <button className="btn primary small" onClick={() => set({ closeup: r.id })} data-testid="zoom-in"><Icon name="zoom" size={16} />{t("Zoom in")}</button>}
        <button className="btn small" onClick={() => set({ tool: "furnish", trayTab: "furniture" })}><Icon name="furnish" size={16} />{t("Furnish")}</button>
        <button className="btn small ghost" onClick={() => set({ placing: { kind: "door", roomId: r.id } })} data-testid="move-door" title={t("Click the wall edge for the new door")}>{t("Move door")}</button>
        {canDemolish && !confirm && <button className="btn small ghost" style={{ color: "var(--bad)", padding: "0 8px" }} onClick={() => setConfirm(true)} data-testid="demolish">{t("Demolish")}</button>}
      </div>
      {canDemolish && confirm && (
        <div className="row" style={{ marginTop: 10, fontSize: "0.85rem", flexWrap: "wrap" }}>
          <span className="bad" style={{ flex: 1 }}>{t("Knock it down? Furniture is lost.")}</span>
          <button className="btn small" onClick={() => setConfirm(false)}>{t("Keep")}</button>
          <button className="btn small primary" onClick={() => { void cmd({ type: "demolishRoom", roomId: r.id }); set({ selectedRoom: null }); setConfirm(false); }} data-testid="demolish-confirm">{t("Demolish")}</button>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ right: a building next door, for sale

export function LotCard() {
  const kind = useGame((s) => s.selectedLot);
  const game = useGame((s) => s.game);
  const set = useGame((s) => s.set);
  const cmd = useGame((s) => s.cmd);
  const toast = useGame((s) => s.toast);
  const co = me(game);
  if (!kind || !co || !game) return null;
  const a = C.annex[kind];
  if (!a || (co.annexes ?? []).some((x) => x.kind === kind)) return null;
  const order = ["garage", "office", "studio", "hq"];
  const ready = order.indexOf(co.level) >= order.indexOf(a.level);
  const idx = priceIndex(game.week);
  const cost = a.cost * idx;
  const why = !ready ? t("Needs a {level} first", { level: t(C.level[a.level].name).toLowerCase() }) : co.cash < cost ? t("You need {amount} more", { amount: money(cost - co.cash) }) : null;
  const buy = async () => {
    const r = await cmd({ type: "buyAnnex", annex: a.id });
    if (!r.ok) return toast({ title: t("Can't buy it yet"), text: r.error, tone: "warn" });
    toast({ title: t("The {place} is yours", { place: t(a.name).toLowerCase() }), text: t("Open Build to add rooms."), tone: "good" });
    set({ selectedLot: null, tool: "build" });
  };
  return (
    <div className="panel room-card" data-testid="lot-card">
      <div className="spread" style={{ alignItems: "flex-start" }}>
        <h3 className="display" style={{ margin: 0, fontSize: "1.5rem", minWidth: 0 }} title={`${t("For sale next door")}: ${a.blurb}`}>{t(a.name)}</h3>
        <button className="icon-btn" style={{ width: 30, height: 30, flex: "none" }} onClick={() => set({ selectedLot: null })} aria-label={t("Close")} data-testid="lot-close"><Icon name="close" size={14} /></button>
      </div>
      <div className="ichips" style={{ marginTop: 8 }}>
        <span className="ichip" title={t("People")}><Icon name="staff" size={14} />+{a.staffCap}</span>
        <span className="ichip" title={t("Tiles")}><Icon name="build" size={14} />{a.tilesW} × {a.tilesH}</span>
        <span className="ichip" title={t("Rooms: {rooms} · rent {rent} a week", { rooms: a.rooms.map((x) => t(C.room[x].name)).join(", "), rent: money(a.rent * idx) })}><Icon name="coin" size={14} color="var(--gold)" />{t("{money}/wk", { money: money(a.rent * idx) })}</span>
      </div>
      <div className="row" style={{ marginTop: 12, flexWrap: "wrap" }}>
        <button className="btn primary small" disabled={!!why} title={why ?? undefined} onClick={() => void buy()} data-testid="lot-buy"><Icon name="coin" size={16} />{t("Buy")} {money(cost)}</button>
        {why && <span className="bad" style={{ fontSize: "0.82rem" }}>{why}</span>}
      </div>
    </div>
  );
}

/** Keyboard: Space pauses, 1-4 set speed, R rotates, Esc backs out. */
export function useHotkeys() {
  const [, force] = useState(0);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.closest("input, textarea, select")) return;
      const s = useGame.getState();
      if (e.key === " ") {
        e.preventDefault();
        if (s.speed) s.setSpeed(0);
        else s.resume();
      } else if (["1", "2", "3", "4"].includes(e.key) && !s.screen) s.setSpeed(Number(e.key));
      else if (e.key.toLowerCase() === "r" && s.placing?.kind === "furniture") s.set({ rot: (s.rot + 1) % 2 });
      else if (e.key === "Escape") {
        if (s.placing) s.set({ placing: null, buildGhost: null });
        else if (s.closeup) s.set({ closeup: null });
        else if (s.tool) s.set({ tool: null });
        else if (s.selectedRoom) s.set({ selectedRoom: null });
        else if (s.selectedLot) s.set({ selectedLot: null });
      }
      force((x) => x + 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
}

export function useCompany(): Company | null {
  return me(useGame((s) => s.game));
}

/** A yearly goal in the player's language: its text from the content, with the target filled in. */
function goalText(g: { id: string; text: string; target: number }): string {
  const def = C.goal[g.id];
  if (!def) return t(g.text);
  if (def.text.includes("top {n}") && g.target === 1) return t("Finish the year as the biggest publisher in the league");
  return t(def.text, { n: def.metric === "copiesYear" ? g.target.toLocaleString("en-US") : g.target });
}

/** Who is on a project: a people icon and "4" on the row, "4 on it · led by Mo (level 7)" on hover. */
function teamLine(team: { name: string; level: number; founder?: boolean }[]): { n: number; title: string } | undefined {
  if (!team.length) return undefined;
  const lead = [...team].sort((a, b) => b.level - a.level)[0];
  const first = lead.founder ? t("You") : lead.name.split(" ")[0];
  const v = { n: team.length, name: first, level: lead.level };
  return team.length === 1
    ? { n: 1, title: t("{name} on it (level {level})", v) }
    : { n: team.length, title: t("{n} on it · led by {name} (level {level})", v) };
}

const FX_WORDS: Record<string, (v: number) => string> = {
  morale: (v) => t("+{v} morale nearby", { v }), speed: (v) => t("+{v}% work speed", { v: Math.round(v * 100) }), quality: (v) => t("+{v}% quality", { v: Math.round(v * 100) }),
  detect: (v) => t("spots {v}% more problems", { v: Math.round(v * 100) }), fans: (v) => t("+{v}% fans", { v: Math.round(v * 100) }), hype: (v) => t("+{v}% hype", { v: Math.round(v * 100) }),
  printQuality: (v) => t("+{v}% print quality", { v: Math.round(v * 100) }), printSpeed: (v) => t("prints {v}% faster", { v: Math.round(v * 100) }), logistics: (v) => t("{v}% cheaper storage", { v: Math.round(v * 100) }),
  directSales: (v) => t("{v}% sold direct", { v: Math.round(v * 100) }), petMorale: (v) => t("+{v} morale from pets", { v }), legacy: () => t("keeps old games selling"),
};

type Fact = { icon: string; v: string; title: string };
const FX_ICONS: Record<string, [string, (v: number) => string]> = {
  morale: ["heart", (v) => `+${v}`], speed: ["ff", (v) => `+${Math.round(v * 100)}%`], quality: ["star", (v) => `+${Math.round(v * 100)}%`],
  detect: ["zoom", (v) => `+${Math.round(v * 100)}%`], fans: ["fans", (v) => `+${Math.round(v * 100)}%`], hype: ["trend", (v) => `+${Math.round(v * 100)}%`],
  printQuality: ["print", (v) => `+${Math.round(v * 100)}%`], printSpeed: ["print", (v) => `+${Math.round(v * 100)}%`], logistics: ["box", (v) => `−${Math.round(v * 100)}%`],
  directSales: ["coin", (v) => `${Math.round(v * 100)}%`], petMorale: ["pet", (v) => `+${v}`], legacy: ["replay", () => ""],
};

/** What a piece of furniture does, as icons and numbers (heart +3, star +2%); the words are on hover. */
function furnitureFacts(f: { fx: Record<string, number | undefined>; seat?: boolean; storage?: number }): Fact[] {
  const out: Fact[] = [];
  if (f.seat) out.push({ icon: "staff", v: "1", title: t("a desk for one") });
  if (f.storage) out.push({ icon: "box", v: String(f.storage), title: tp(f.storage, "holds {n} pallet", "holds {n} pallets") });
  for (const [k, v] of Object.entries(f.fx)) if (typeof v === "number" && v && FX_ICONS[k] && FX_WORDS[k]) out.push({ icon: FX_ICONS[k][0], v: FX_ICONS[k][1](v), title: FX_WORDS[k](v) });
  return out;
}

/** One short line on what a piece of furniture does ("+3 morale nearby · +2% quality"): the card's tooltip. */
function furnitureNote(f: { fx: Record<string, number | undefined>; seat?: boolean; storage?: number; light?: number }): string {
  const parts: string[] = [];
  if (f.seat) parts.push(t("a desk for one"));
  if (f.storage) parts.push(tp(f.storage, "holds {n} pallet", "holds {n} pallets"));
  for (const [k, v] of Object.entries(f.fx)) if (typeof v === "number" && v && FX_WORDS[k]) parts.push(FX_WORDS[k](v));
  return parts.join(" · ") || t("Decor: makes the room feel lived in");
}
